import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-14fc9fce"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "calendar" }
const _hoisted_2 = { class: "calendar-header" }
const _hoisted_3 = ["disabled"]
const _hoisted_4 = ["disabled"]
const _hoisted_5 = { class: "calendar-weekdays" }
const _hoisted_6 = ["data-testid"]
const _hoisted_7 = { class: "calendar-body" }
const _hoisted_8 = ["data-testid"]
const _hoisted_9 = ["onClick", "data-testid"]
const _hoisted_10 = { class: "date-number" }
const _hoisted_11 = {
  key: 0,
  class: "work-time"
}
const _hoisted_12 = { key: 0 }

import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { DesiredWorkSchedule } from "@/types/desired_work_schedule/DesiredWorkSchedule";
import {
  addMonths,
  calcDate,
  endOfMonth,
  formatFromISOString,
  getDate,
  getDay,
  getMonthIndex,
  getYear,
  now,
  parseISO,
  subDays
} from "@/utils/date";
import { computed, ref } from "vue";

// カレンダー日付の型定義
interface CalendarDay {
  day: number; // 日付の数値部分（1-31）
  date: string; // YYYY-MM-DD形式の文字列
  isCurrentMonth: boolean; // 現在表示中の月かどうか
  isToday: boolean; // 今日の日付かどうか
  isHoliday: boolean; // 祝日かどうか
  desiredWorkSchedule?: DesiredWorkSchedule; // 「働きたい日」情報
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Calendar',
  props: {
  desiredWorkSchedules: {
    type: Array as () => DesiredWorkSchedule[],
    default: () => []
  },
  holidays: {
    type: Array as () => string[],
    default: () => []
  }
},
  emits: ["dayClick"],
  setup(__props, { emit }) {

const props = __props;





const nowValue = now();

// 現在表示中の年月
const currentYear = ref(getYear(nowValue));
const currentMonth = ref(getMonthIndex(nowValue) + 1);
const selectedDate = ref<string | null>(null);

// 表示可能な最小・最大日付を計算（前後2ヶ月まで）
const minDate = computed(() => {
  const d = addMonths(nowValue, -2);
  const year = getYear(d);
  const month = getMonthIndex(d) + 1;
  return { year, month };
});
const maxDate = computed(() => {
  const d = addMonths(nowValue, 2);
  const year = getYear(d);
  const month = getMonthIndex(d) + 1;
  return { year, month };
});

const isPrevMonthDisabled = computed(() => {
  const currentYearMonth = formatYearMonth(
    currentYear.value,
    currentMonth.value
  );
  const minYearMonth = formatYearMonth(minDate.value.year, minDate.value.month);
  return currentYearMonth <= minYearMonth;
});

const isNextMonthDisabled = computed(() => {
  const currentYearMonth = formatYearMonth(
    currentYear.value,
    currentMonth.value
  );
  const maxYearMonth = formatYearMonth(maxDate.value.year, maxDate.value.month);
  return currentYearMonth >= maxYearMonth;
});

// 月の最初と最後の日を計算
const firstDayOfMonth = computed(() => {
  return calcDate(currentYear.value, currentMonth.value, 1);
});
const lastDayOfMonth = computed(() => {
  return endOfMonth(calcDate(currentYear.value, currentMonth.value, 1));
});

// カレンダーの日付配列を生成
const calendarDays = computed((): CalendarDay[] => {
  const days: CalendarDay[] = [];

  // 月の最初の日の曜日を取得（1: 月曜日, 2: 火曜日, ...）
  const firstDayOfWeek = getDay(firstDayOfMonth.value);

  // 前月の日を追加
  const prevMonthLastDate = getDate(subDays(firstDayOfMonth.value, 1));

  // 前月の年と月を計算
  const prevMonthYear =
    currentMonth.value === 1 ? currentYear.value - 1 : currentYear.value;
  const prevMonth = currentMonth.value === 1 ? 12 : currentMonth.value - 1;

  // 初週が前月の日付で埋まるようであれば前月の日付の追加はスキップし、カレンダーの初週が空になることを防ぐ
  if (firstDayOfWeek % 7 !== 0) {
    for (let i = 0; i < firstDayOfWeek; i++) {
      const dayNumber = prevMonthLastDate - firstDayOfWeek + i + 1;
      const date = formatDateString(prevMonthYear, prevMonth, dayNumber);

      days.push({
        day: dayNumber,
        date,
        isCurrentMonth: false,
        isToday: false,
        isHoliday: isHoliday(date)
      });
    }
  }

  // 今月の日を追加
  const lastDate = getDate(lastDayOfMonth.value);
  const isCurrentMonthToday =
    getYear(nowValue) === currentYear.value &&
    getMonthIndex(nowValue) === currentMonth.value - 1;

  for (let i = 1; i <= lastDate; i++) {
    const date = formatDateString(currentYear.value, currentMonth.value, i);
    const desiredWorkSchedule = props.desiredWorkSchedules.find(
      schedule => schedule.date === date
    );

    days.push({
      day: i,
      date,
      isCurrentMonth: true,
      isToday: isCurrentMonthToday && getDate(nowValue) === i,
      isHoliday: isHoliday(date),
      desiredWorkSchedule
    });
  }

  // 次月の年と月を計算
  const nextMonthYear =
    currentMonth.value === 12 ? currentYear.value + 1 : currentYear.value;
  const nextMonth = currentMonth.value === 12 ? 1 : currentMonth.value + 1;

  // 次月の日を追加（6週間表示するために必要な場合）
  const daysNeeded = 42 - days.length; // 6週間分（42日）
  for (let i = 1; i <= daysNeeded; i++) {
    const date = formatDateString(nextMonthYear, nextMonth, i);

    days.push({
      day: i,
      date,
      isCurrentMonth: false,
      isToday: false,
      isHoliday: isHoliday(date)
    });
  }

  return days;
});

// 週ごとに分割した日付配列
const calendarWeeks = computed((): CalendarDay[][] => {
  const weeks: CalendarDay[][] = [];
  for (let i = 0; i < calendarDays.value.length; i += 7) {
    weeks.push(calendarDays.value.slice(i, i + 7));
  }
  return weeks;
});

const formatYearMonth = (year: number, month: number): string => {
  return `${year}${String(month).padStart(2, "0")}`;
};

const formatDateString = (year: number, month: number, day: number): string => {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
    2,
    "0"
  )}`;
};

// 祝日かどうかをチェックするヘルパー関数
const isHoliday = (date: string): boolean => {
  return props.holidays.includes(date);
};

// 前月へ移動（制限範囲内のみ）
const goToPrevMonth = () => {
  if (isPrevMonthDisabled.value) return; // 範囲外なら何もしない

  if (currentMonth.value === 1) {
    currentYear.value--;
    currentMonth.value = 12;
  } else {
    currentMonth.value--;
  }
};

// 次月へ移動（制限範囲内のみ）
const goToNextMonth = () => {
  if (isNextMonthDisabled.value) return; // 範囲外なら何もしない

  if (currentMonth.value === 12) {
    currentYear.value++;
    currentMonth.value = 1;
  } else {
    currentMonth.value++;
  }
};

// 日付クリックハンドラを修正
const handleDayClick = (day: CalendarDay) => {
  selectedDate.value = day.date;

  const todayString = formatDateString(
    getYear(nowValue),
    getMonthIndex(nowValue) + 1,
    getDate(nowValue)
  );
  if (day.isCurrentMonth && day.date >= todayString) {
    emit("dayClick", day.date);
  }
};

// 日付が現在選択されているかを確認するヘルパー関数
const isSelectedDate = (day: CalendarDay): boolean => {
  if (!selectedDate.value || !day.isCurrentMonth) return false;
  return day.date === selectedDate.value;
};

// 時間表示用のヘルパー関数
const formatBeginTime = (schedule: DesiredWorkSchedule): string => {
  if (schedule.isAllDay) {
    return "終日";
  }

  return formatFromISOString(schedule.startAt!, "HH:mm");
};

const formatEndTime = (schedule: DesiredWorkSchedule): string => {
  if (schedule.isAllDay) {
    return "";
  }

  const finishTimeStr = formatFromISOString(schedule.finishAt!, "HH:mm");

  const startTime = parseISO(schedule.startAt!);
  const finishTime = parseISO(schedule.finishAt!);

  // 終了時間と開始時間が同じ日付でない場合、翌日の時間として表示する
  if (getDay(finishTime) !== getDay(startTime)) {
    return `翌${finishTimeStr}`;
  }

  return finishTimeStr;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("button", {
        onClick: goToPrevMonth,
        class: _normalizeClass(["month-nav", { disabled: isPrevMonthDisabled.value }]),
        disabled: isPrevMonthDisabled.value
      }, [
        _createVNode(FeatherIcon, {
          icon: "chevron-left",
          size: "24"
        })
      ], 10, _hoisted_3),
      _createTextVNode(),
      _createElementVNode("h3", null, _toDisplayString(currentYear.value) + "年" + _toDisplayString(currentMonth.value) + "月\n      ", 1),
      _createTextVNode(),
      _createElementVNode("button", {
        onClick: goToNextMonth,
        class: _normalizeClass(["month-nav", { disabled: isNextMonthDisabled.value }]),
        disabled: isNextMonthDisabled.value
      }, [
        _createVNode(FeatherIcon, {
          icon: "chevron-right",
          size: "24"
        })
      ], 10, _hoisted_4)
    ]),
    _createTextVNode(),
    _createElementVNode("div", _hoisted_5, [
      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(['日', '月', '火', '水', '木', '金', '土'], (day, index) => {
        return _createElementVNode("div", {
          key: 'weekday-' + index,
          class: _normalizeClass(["weekday", { sunday: index === 0, saturday: index === 6 }]),
          "data-testid": `weekday-${index}`
        }, _toDisplayString(day), 11, _hoisted_6)
      }), 64))
    ]),
    _createTextVNode(),
    _createElementVNode("div", _hoisted_7, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(calendarWeeks.value, (week, weekIndex) => {
        return (_openBlock(), _createElementBlock("div", {
          key: 'week-' + weekIndex,
          class: "calendar-week",
          "data-testid": `calendar-week-${weekIndex}`
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(week, (day, dayIndex) => {
            return (_openBlock(), _createElementBlock("div", {
              key: 'day-' + weekIndex + '-' + dayIndex,
              class: _normalizeClass(["calendar-day", {
            'other-month': !day.isCurrentMonth,
            today: day.isToday,
            'desired-work-day': day.desiredWorkSchedule && day.isCurrentMonth,
            sunday: dayIndex === 0,
            saturday: dayIndex === 6,
            holiday: day.isHoliday, // 祝日クラスを追加
            selected: isSelectedDate(day)
          }]),
              onClick: ($event: any) => (handleDayClick(day)),
              "data-testid": `calendar-day-${day.date}`
            }, [
              _createElementVNode("span", _hoisted_10, _toDisplayString(day.isCurrentMonth ? day.day : ""), 1),
              _createTextVNode(),
              (day.desiredWorkSchedule && day.isCurrentMonth)
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                    _createElementVNode("div", null, _toDisplayString(formatBeginTime(day.desiredWorkSchedule)), 1),
                    _createTextVNode(),
                    (!day.desiredWorkSchedule.isAllDay)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_12, _toDisplayString(formatEndTime(day.desiredWorkSchedule)), 1))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true)
            ], 10, _hoisted_9))
          }), 128))
        ], 8, _hoisted_8))
      }), 128))
    ])
  ]))
}
}

})